/**
 * Pure view model of the «Статусы менеджера» dictionary (Settings, contract
 * #28/#28.1/#28.2 — ADDITIVE v1.8.0, wave 9, T.Y9, Р9/D17).
 *
 * Framework-free on purpose (the `unit` Playwright project runs in plain Node):
 * the api layer is imported TYPE-ONLY. Unwrapping, ordering, validation and the
 * one sentence the no-delete rule prints live here, so the tab stays a renderer.
 */

import type { ManagerStatusDto } from '../../api/manager-statuses';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Unwraps #28. `{ data: { statuses: […] } }` is the contract shape; a bare array
 * and `{ statuses: […] }` unwrap too. Anything else is an EMPTY dictionary — an
 * unexpected payload must not read as "every status was deleted", and a deleted
 * status is a thing the api refuses to have.
 */
export function parseStatuses(body: unknown): ManagerStatusDto[] {
  if (Array.isArray(body)) return body as ManagerStatusDto[];
  if (!isRecord(body)) return [];
  const data = isRecord(body['data']) ? body['data'] : body;
  if (Array.isArray(data)) return data as ManagerStatusDto[];
  if (isRecord(data) && Array.isArray(data['statuses'])) return data['statuses'] as ManagerStatusDto[];
  return [];
}

/**
 * The display order = the api's `sortOrder`, then label, then id. The api already
 * orders by `sortOrder`; this re-asserts it defensively for a stub and makes the
 * tie-break deterministic (two renders of the same data, one order).
 */
export function sortStatuses(rows: ReadonlyArray<ManagerStatusDto>): ManagerStatusDto[] {
  return [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'ru') || a.id - b.id);
}

/** A label is a trimmed, non-empty, ≤ 80-char string — a dictionary entry, not a paragraph. */
export const STATUS_LABEL_MAX = 80;

export function normalizeStatusLabel(value: string): string | null {
  const trimmed = value.trim().replace(/\s+/g, ' ');
  return trimmed === '' ? null : trimmed.slice(0, STATUS_LABEL_MAX);
}

export function statusLabelError(value: string): string | undefined {
  if (value.trim() === '') return 'Введите название статуса.';
  if (normalizeStatusLabel(value) === null) return 'Введите название статуса.';
  return undefined;
}

/**
 * The client-side courtesy for the api's 409: a label another row already holds.
 * Case-insensitive on purpose — the api's uniqueness is on the stored label, and a
 * manager typing «Перезвонить» over an existing «перезвонить» must see the same
 * refusal the server would answer, before a doomed request leaves the page.
 */
export function isDuplicateLabel(label: string, rows: ReadonlyArray<ManagerStatusDto>, exceptId?: number): boolean {
  const wanted = normalizeStatusLabel(label);
  if (wanted === null) return false;
  return rows.some(
    (row) => row.id !== exceptId && row.label.trim().toLowerCase() === wanted.toLowerCase(),
  );
}

/** The hint under the list — the no-delete rule, in the api's own words (D17). */
export const STATUS_NO_DELETE_HINT = 'Статус с историей нельзя удалить, только деактивировать.';

/** The greyed row's marker: the entry stays, new assignments are refused (#5.6 answers 400). */
export const STATUS_INACTIVE_LABEL = 'деактивирован';

/** `true` = the toggle's next click DEactivates; the caption names the action, never the state. */
export function statusToggleLabel(row: Pick<ManagerStatusDto, 'active'>): string {
  return row.active ? 'Деактивировать' : 'Включить';
}

/**
 * Which rows the dictionary can ASSIGN further: only the active ones. The tab
 * shows the inactive rows greyed («деактивирован»), and the application picker —
 * a different surface — reads the same `active` flag to hide them.
 */
export function activeStatuses(rows: ReadonlyArray<ManagerStatusDto>): ManagerStatusDto[] {
  return rows.filter((row) => row.active);
}

/**
 * The rename draft is worth a PATCH only when it differs from the stored label —
 * a save that would send an unchanged label is a no-op, not a write.
 */
export function statusLabelChanged(row: Pick<ManagerStatusDto, 'label'>, draft: string): boolean {
  const next = normalizeStatusLabel(draft);
  return next !== null && next !== row.label;
}
